import { useState, useEffect } from "react";
import axios from "axios";
import { Check, CheckCheck, Play, Pause, Image as ImageIcon, Download, Clock, X, CornerDownRight } from "lucide-react";
import { Avatar } from "@/components/Avatar";
import { MessageActions, ReactionsBar, MessageHistoryModal } from "@/components/chat/MessageActions";
import { STICKER_SET } from "@/components/chat/EmojiPicker";
import { ChatCard } from "@/components/chat/ChatMiniApps";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Ticks = ({ msg, isOwn }) => {
  if (!isOwn) return null;
  if (msg.read_at) return <CheckCheck data-testid={`msg-read-${msg.id}`} className="w-3 h-3 text-sky-400" />;
  if (msg.delivered_at) return <Check data-testid={`msg-delivered-${msg.id}`} className="w-3 h-3 text-sky-400" />;
  return <Check className="w-3 h-3 text-neutral-500" />;
};

export const ChatMessage = ({
  msg, isOwn, authHeader, mediaEndpointPrefix, viewerRole = "user",
  peerInfo, selfInfo, allMessages = [], onReply,
}) => {
  const [mediaDataUrl, setMediaDataUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [audioEl, setAudioEl] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [enlarged, setEnlarged] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editBody, setEditBody] = useState(msg.body || "");
  const [historyOpen, setHistoryOpen] = useState(false);

  const loadMedia = async () => {
    if (mediaDataUrl || loading) return null;
    setLoading(true);
    try {
      const r = await axios.get(`${API}${mediaEndpointPrefix}/${msg.id}/media`, { headers: authHeader() });
      const url = `data:${r.data.media_mime || "application/octet-stream"};base64,${r.data.media_b64}`;
      setMediaDataUrl(url);
      return url;
    } catch { /* ignore */ } finally { setLoading(false); }
    return null;
  };

  // Auto-load image media when the bubble mounts so receipts and photos
  // preview inline without the user tapping "Ver imagen". Voice notes are
  // NOT auto-loaded (audio blobs are heavier and less useful without user
  // intent). We also skip auto-load if the user is waiting for admin
  // approval to view the image.
  useEffect(() => {
    if (msg.kind !== "image") return;
    if (mediaDataUrl || loading) return;
    // Waiting-for-approval state: don't leak media.
    const canView = viewerRole === "admin" || isOwn || msg.download_approved;
    if (!canView) return;
    loadMedia();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [msg.id, msg.kind, msg.download_approved, viewerRole, isOwn]);

  const togglePlay = async () => {
    let url = mediaDataUrl;
    if (!url) url = await loadMedia();
    if (!url) return;
    if (audioEl) {
      if (playing) { audioEl.pause(); setPlaying(false); }
      else { audioEl.play(); setPlaying(true); }
      return;
    }
    const a = new Audio(url);
    a.onended = () => setPlaying(false);
    a.onpause = () => setPlaying(false);
    a.onplay = () => setPlaying(true);
    setAudioEl(a);
    a.play();
  };

  const requestDownload = async () => {
    if (requesting) return;
    setRequesting(true);
    try { await axios.post(`${API}/users/messages/${msg.id}/request-download`, {}, { headers: authHeader() }); } catch { /* ignore */ }
    finally { setRequesting(false); }
  };
  const approveDownload = async () => {
    try { await axios.post(`${API}/admin/messages/${msg.id}/approve-download`, {}, { headers: authHeader() }); } catch { /* ignore */ }
  };
  const triggerDownload = async () => {
    let url = mediaDataUrl;
    if (!url) url = await loadMedia();
    if (!url) return;
    const a = document.createElement("a");
    a.href = url;
    a.download = `crisangel-${msg.id}.${(msg.media_mime || "image/png").split("/")[1] || "png"}`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  const reactToggle = async (emoji) => {
    const url = viewerRole === "admin"
      ? `${API}/admin/messages/${msg.id}/react`
      : `${API}/users/messages/${msg.id}/react`;
    try { await axios.post(url, { emoji }, { headers: authHeader() }); } catch { /* ignore */ }
  };

  const saveEdit = async () => {
    const url = viewerRole === "admin"
      ? `${API}/admin/messages/${msg.id}`
      : `${API}/users/messages/${msg.id}`;
    try {
      await axios.patch(url, { body: editBody }, { headers: authHeader() });
      setEditing(false);
    } catch { /* ignore */ }
  };

  const doDelete = async () => {
    if (!window.confirm("¿Eliminar este mensaje?")) return;
    const url = viewerRole === "admin"
      ? `${API}/admin/messages/${msg.id}`
      : `${API}/users/messages/${msg.id}`;
    try { await axios.delete(url, { headers: authHeader() }); } catch (e) {
      alert(e?.response?.data?.detail || "Error eliminando");
    }
  };

  const bubble = isOwn
    ? "bg-[#d4af37]/15 border border-[#d4af37]/40 text-white rounded-br-sm"
    : "bg-black/60 border border-white/10 text-neutral-100 rounded-bl-sm";

  const sender = isOwn ? selfInfo : peerInfo;
  const senderId = sender?.id;
  const senderName = sender?.name || (isOwn ? "Tú" : "Cristopher");
  const canDownload = msg.kind === "image" && (viewerRole === "admin" || isOwn || msg.download_approved);
  const isWaitingApproval = msg.kind === "image" && viewerRole === "user" && !isOwn && !msg.download_approved;
  const adminCanApprove = msg.kind === "image" && viewerRole === "admin" && !msg.download_approved && !isOwn;

  // Look up the replied-to message for in-bubble preview
  const repliedTo = msg.reply_to ? allMessages.find((m) => m.id === msg.reply_to) : null;
  const repliedPreview = repliedTo
    ? (repliedTo.deleted ? "[mensaje eliminado]"
      : repliedTo.kind === "image" ? "🖼️ Imagen"
      : repliedTo.kind === "voice" ? "🎙️ Nota de voz"
      : repliedTo.kind === "sticker" ? "💟 Sticker"
      : (repliedTo.body || "").slice(0, 80))
    : null;

  // Deleted state
  if (msg.deleted) {
    return (
      <div className={`flex ${isOwn ? "justify-end" : "justify-start"} gap-1.5 items-end opacity-70`} data-testid={`msg-${msg.id}`}>
        {!isOwn && senderId && <Avatar userId={senderId} name={senderName} authHeader={authHeader} hasPicHint={sender?.has_profile_pic} size={22} className="flex-shrink-0 mb-0.5" />}
        <div className="bg-black/40 border border-white/5 rounded-2xl px-2.5 py-1.5 text-[11px] italic text-neutral-500">
          ⊘ Mensaje eliminado
          {viewerRole === "admin" && (
            <button data-testid={`history-${msg.id}`} onClick={() => setHistoryOpen(true)} className="ml-2 text-amber-300 hover:underline not-italic">ver historial</button>
          )}
        </div>
        {historyOpen && <MessageHistoryModal mid={msg.id} authHeader={authHeader} onClose={() => setHistoryOpen(false)} />}
      </div>
    );
  }

  return (
    <div className={`group relative flex ${isOwn ? "justify-end" : "justify-start"} gap-1.5 items-end`} data-testid={`msg-${msg.id}`}>
      {!isOwn && senderId && (
        <Avatar userId={senderId} name={senderName} authHeader={authHeader} hasPicHint={sender?.has_profile_pic} size={22} className="flex-shrink-0 mb-0.5" />
      )}
      <div className={`relative ${msg.cards?.some((c) => c.app) && viewerRole === "user" ? "max-w-[92%] w-full" : "max-w-[80%]"}`}>
        {/* Hover actions */}
        <MessageActions
          msg={msg}
          isOwn={isOwn}
          viewerRole={viewerRole}
          authHeader={authHeader}
          onReact={reactToggle}
          onReply={() => onReply?.(msg)}
          onEdit={() => { setEditBody(msg.body || ""); setEditing(true); }}
          onDelete={doDelete}
          onShowHistory={() => setHistoryOpen(true)}
        />

        <div className={`rounded-2xl px-2.5 py-1.5 text-[11px] whitespace-pre-wrap ${bubble}`}>
          {/* Replied-to preview */}
          {repliedTo && (
            <div className="border-l-2 border-[#d4af37]/60 pl-2 mb-1 text-[10px] text-neutral-400 italic">
              <div className="flex items-center gap-1 text-[#d4af37]/80 text-[9px] uppercase tracking-widest mb-0.5"><CornerDownRight className="w-2.5 h-2.5" /> respondiendo</div>
              <div className="truncate">{repliedPreview}</div>
            </div>
          )}

          {/* Sticker */}
          {msg.kind === "sticker" && msg.sticker && (
            <div data-testid={`sticker-msg-${msg.id}`} className="text-6xl leading-none py-1">{STICKER_SET[msg.sticker] || "💟"}</div>
          )}

          {/* Image */}
          {msg.kind === "image" && (
            <div className="mb-1">
              <button type="button" onClick={async () => { await loadMedia(); setEnlarged(true); }} className="block">
                {mediaDataUrl ? (
                  <img src={mediaDataUrl} alt="img" className="rounded-lg max-w-[200px] max-h-[200px] object-cover hover:opacity-90 transition" />
                ) : (
                  <span className="inline-flex items-center gap-1 text-sky-300 underline">
                    <ImageIcon className="w-3 h-3" /> {loading ? "Cargando..." : "Ver imagen"}
                  </span>
                )}
              </button>
              <div className="flex items-center gap-1.5 mt-1">
                {canDownload && (
                  <button data-testid={`msg-dl-${msg.id}`} onClick={triggerDownload} className="text-[10px] inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/30">
                    <Download className="w-2.5 h-2.5" /> Descargar
                  </button>
                )}
                {/* Receipt PDF: backend already authorises by ownership/admin,
                   so the button is shown whenever the message carries a
                   receipt — no separate PNG-approval gate is needed. */}
                {msg.receipt?.number && (
                  <a
                    data-testid={`msg-dl-pdf-${msg.id}`}
                    href={`${API}/users/messages/${msg.id}/receipt.pdf`}
                    onClick={async (e) => {
                      // Use fetch with auth header so we don't lose the JWT
                      e.preventDefault();
                      try {
                        const r = await axios.get(
                          `${API}/users/messages/${msg.id}/receipt.pdf`,
                          { headers: authHeader?.() || {}, responseType: "blob" },
                        );
                        const blob = new Blob([r.data], { type: "application/pdf" });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement("a");
                        a.href = url;
                        a.download = `recibo-${msg.receipt.number}.pdf`;
                        a.click();
                        URL.revokeObjectURL(url);
                      } catch (err) {
                        // eslint-disable-next-line no-console
                        console.error("[receipt] pdf download failed:", err);
                      }
                    }}
                    className="text-[10px] inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-[#d4af37]/20 text-[#d4af37] border border-[#d4af37]/30 hover:bg-[#d4af37]/30"
                  >
                    <Download className="w-2.5 h-2.5" /> PDF
                  </a>
                )}
                {isWaitingApproval && (
                  <button data-testid={`msg-req-dl-${msg.id}`} onClick={requestDownload} disabled={requesting || !!msg.download_requested_at} className="text-[10px] inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/30 hover:bg-amber-500/30 disabled:opacity-60">
                    <Clock className="w-2.5 h-2.5" /> {msg.download_requested_at ? "Solicitado" : "Pedir descarga"}
                  </button>
                )}
                {adminCanApprove && (
                  <button data-testid={`msg-approve-dl-${msg.id}`} onClick={approveDownload} className="text-[10px] inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-sky-500/20 text-sky-300 border border-sky-500/30 hover:bg-sky-500/30">
                    <Check className="w-2.5 h-2.5" /> {msg.download_requested_at ? "Aprobar descarga" : "Permitir descarga"}
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Voice */}
          {msg.kind === "voice" && (
            <button onClick={togglePlay} type="button" className="inline-flex items-center gap-1.5 px-1.5 py-0.5 rounded-full bg-black/30 border border-white/10 mb-1">
              {playing ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
              <span className="text-[10px] font-mono">{msg.duration_sec ? `${Math.round(msg.duration_sec)}s` : "Nota de voz"}</span>
            </button>
          )}

          {/* Text — editable inline */}
          {editing ? (
            <div className="space-y-1">
              <textarea
                data-testid={`edit-textarea-${msg.id}`}
                value={editBody}
                onChange={(e) => setEditBody(e.target.value)}
                className="w-full bg-black/40 border border-white/20 rounded text-white text-[11px] p-1 resize-none"
                rows={2}
              />
              <div className="flex gap-1">
                <button data-testid={`edit-save-${msg.id}`} onClick={saveEdit} className="text-[10px] px-2 py-0.5 rounded bg-[#d4af37] text-black">Guardar</button>
                <button onClick={() => setEditing(false)} className="text-[10px] px-2 py-0.5 rounded bg-white/10 text-neutral-300">Cancelar</button>
              </div>
            </div>
          ) : (
            msg.body && <div>{msg.body}</div>
          )}

          {msg.is_broadcast && <div className="text-[9px] text-purple-300/80 mt-0.5 italic">📣 Mensaje masivo</div>}
          {Array.isArray(msg.cards) && msg.cards.length > 0 && (
            <div className={`mt-2 flex ${viewerRole === "user" ? "flex-col" : "flex-wrap"} gap-1.5`} data-testid={`ai-cards-${msg.id}`}>
              {msg.cards.map((c) => <ChatCard key={c.type} card={c} msgId={msg.id} createdAt={msg.created_at} viewerRole={viewerRole} collapsed={!!c.app && allMessages.some((m) => m.id !== msg.id && m.created_at > msg.created_at && (m.cards || []).some((x) => x.type === c.type))} />)}
            </div>
          )}
          {msg.is_ai && <div className="text-[9px] text-[#d4af37]/80 mt-1 italic">✦ Respuesta automática del equipo · Cristopher revisa todo personalmente</div>}
          <div className="flex items-center justify-end gap-1 mt-1">
            {msg.edited && <span className="text-[9px] text-neutral-500 italic">editado</span>}
            <span className="text-[9px] text-neutral-500">{new Date(msg.created_at).toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" })}</span>
            <Ticks msg={msg} isOwn={isOwn} />
          </div>
        </div>
        <ReactionsBar reactions={msg.reactions} onReact={reactToggle} viewerRole={viewerRole} isOwn={isOwn} />
      </div>
      {isOwn && senderId && (
        <Avatar userId={senderId} name={senderName} authHeader={authHeader} hasPicHint={sender?.has_profile_pic} size={22} className="flex-shrink-0 mb-0.5" />
      )}

      {enlarged && mediaDataUrl && (
        <div data-testid={`msg-enlarged-${msg.id}`} onClick={() => setEnlarged(false)} className="fixed inset-0 z-[200] bg-black/85 backdrop-blur-sm flex items-center justify-center p-6 cursor-zoom-out">
          <button onClick={() => setEnlarged(false)} className="absolute top-4 right-4 h-9 w-9 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"><X className="w-4 h-4" /></button>
          <img src={mediaDataUrl} alt="enlarged" className="max-w-[95vw] max-h-[90vh] object-contain rounded-xl shadow-2xl" />
        </div>
      )}

      {historyOpen && <MessageHistoryModal mid={msg.id} authHeader={authHeader} onClose={() => setHistoryOpen(false)} />}
    </div>
  );
};
